import { useNavigate } from "@tanstack/react-router";
import { CircleAlert, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Controller } from "react-hook-form";

import { Button } from "@/components/button";
import { FieldError, Input, Label, TextField } from "@/components/field";

import {
  type PasswordCreationFormData,
  usePasswordCreationForm,
} from "../hooks/usePasswordCreationForm";

interface PasswordCreationStepProps {
  initialData: PasswordCreationFormData;
  isLoading?: boolean;
  onComplete: (data: PasswordCreationFormData) => void;
  onGoBack?: () => void;
  userId: null | number;
}

export function PasswordCreationStep({
  initialData,
  isLoading = false,
  onComplete,
}: PasswordCreationStepProps) {
  const { control, handleSubmit, watch } = usePasswordCreationForm(initialData);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const password = watch("password");

  const onSubmit = async (data: PasswordCreationFormData) => {
    await onComplete(data);

    const searchParams = new URLSearchParams(window.location.search);
    const origin = searchParams.get("origin");

    if (origin === "direct-purchase") {
      try {
        const successData = localStorage.getItem("subscriptionSuccessData");

        if (successData) {
          const parsedData = JSON.parse(successData);

          navigate({
            search: {
              billingCycle: parsedData.billingCycle,
              currency: parsedData.currency,
              origin: origin,
              planName: parsedData.planName,
              price: parsedData.price,
            },
            to: "/subscription-success",
          });

          return;
        }
      } catch (error) {
        console.error("Failed to parse subscription success data:", error);
      }
    }

    navigate({ to: "/" });
  };

  return (
    <form className="flex flex-col gap-3xl" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-xl">
        <div>
          <Controller
            control={control}
            name="password"
            render={({
              field: { name, onBlur, onChange, ref, value },
              fieldState: { error, invalid },
            }) => (
              <TextField
                isInvalid={invalid}
                isRequired
                name={name}
                onBlur={() => {
                  onBlur();
                }}
                onChange={onChange}
                validationBehavior="aria"
                value={value}
              >
                <Label>Password</Label>
                <div className="relative">
                  <Input
                    className={`${invalid ? "pr-16 border-utility-red-500 focus:border-utility-red-500 focus:ring-utility-red-100" : "pr-10"}`}
                    placeholder="Create a password"
                    ref={ref}
                    type={showPassword ? "text" : "password"}
                  />
                  <button
                    className={`absolute inset-y-0 flex items-center ${invalid ? "right-10" : "right-3"}`}
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    type="button"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                    )}
                  </button>
                  {invalid && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <CircleAlert className="w-5 h-5" color="#F04438" />
                    </div>
                  )}
                </div>
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
        </div>

        <Controller
          control={control}
          name="confirmPassword"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              isRequired
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Confirm password</Label>
              <div className="relative">
                <Input
                  className={`${invalid ? "pr-16 border-utility-red-500 focus:border-utility-red-500 focus:ring-utility-red-100" : "pr-10"}`}
                  placeholder="Confirm your password"
                  ref={ref}
                  type={showConfirmPassword ? "text" : "password"}
                />
                <button
                  className={`absolute inset-y-0 flex items-center ${invalid ? "right-10" : "right-3"}`}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  type="button"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                  )}
                </button>
                {invalid && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <CircleAlert className="w-5 h-5" color="#F04438" />
                  </div>
                )}
              </div>
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>

      <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-xs font-semibold text-gray-700 mb-2">
          Password requirements:
        </p>
        <ul className="space-y-2">
          <li className="flex items-center gap-2 text-sm">
            <div
              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                password.length >= 8 ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              {password.length >= 8 && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                </svg>
              )}
            </div>
            <span
              className={
                password.length >= 8
                  ? "text-green-600 font-medium"
                  : "text-gray-600"
              }
            >
              At least 8 characters
            </span>
          </li>
          <li className="flex items-center gap-2 text-sm">
            <div
              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                /[A-Z]/.test(password) && /[a-z]/.test(password)
                  ? "bg-green-500"
                  : "bg-gray-300"
              }`}
            >
              {/[A-Z]/.test(password) && /[a-z]/.test(password) && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                </svg>
              )}
            </div>
            <span
              className={
                /[A-Z]/.test(password) && /[a-z]/.test(password)
                  ? "text-green-600 font-medium"
                  : "text-gray-600"
              }
            >
              Upper and lowercase letters
            </span>
          </li>
          <li className="flex items-center gap-2 text-sm">
            <div
              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                /\d/.test(password) ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              {/\d/.test(password) && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                </svg>
              )}
            </div>
            <span
              className={
                /\d/.test(password)
                  ? "text-green-600 font-medium"
                  : "text-gray-600"
              }
            >
              At least one number
            </span>
          </li>
          <li className="flex items-center gap-2 text-sm">
            <div
              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                /[^\d\sA-Za-z]/.test(password) ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              {/[^\d\sA-Za-z]/.test(password) && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                </svg>
              )}
            </div>
            <span
              className={
                /[^\d\sA-Za-z]/.test(password)
                  ? "text-green-600 font-medium"
                  : "text-gray-600"
              }
            >
              At least one symbol
            </span>
          </li>
        </ul>
      </div>

      <div className="flex justify-center">
        <Button className="w-full" isDisabled={isLoading} type="submit">
          {isLoading ? "Creating password..." : "Confirm"}
        </Button>
      </div>

      <p className="mx-auto text-sm font-medium text-text-secondary hover:text-text-primary text-center">
        Min. 8 characters including an upper and lowercase letter, digit and
        symbol.
      </p>
    </form>
  );
}
